import type { BundleItem } from './bundle-item-row';
import type { BundleConfig } from './config-tab';
import {
  BUNDLE_DEFAULT_SUBHEADING,
  BUNDLE_INDEX_COLUMN_IDS,
  buildBundleIndexRows,
  type BundleIndexRow,
} from '../../../../../../backend/src/matter/subset/bundle/bundle-index-layout';
import type { BundleItemSeed, BundleVariantConfig } from '@/new/components/download/types';

export interface BundlePreviewEntry {
  id: string;
  index: number;
  item: BundleItem;
  row: BundleIndexRow;
}

export interface BundlePreviewStats {
  itemCount: number;
  documentPages: number;
  totalPagesWithContents: number;
}

export function buildDefaultBundleDraft(
  bundleConfig: BundleVariantConfig,
  seeds: BundleItemSeed[],
): {
  items: BundleItem[];
  formConfig: BundleConfig;
} {
  return {
    items: seeds.map((item, index) => ({
      ...item,
      order: index,
      customName: null,
    })),
    formConfig: {
      name: bundleConfig.defaultName,
      subheading: BUNDLE_DEFAULT_SUBHEADING,
      pageNumberPosition: { x: 100, y: 100 },
      fontFamily: 'times',
      fontSize: 12,
      pageNumberSize: 12,
      textColor: 'black',
      pageNumberBold: false,
      pageNumberItalic: false,
      pageNumberUnderline: false,
      numberIndexPages: false,
      useSmallRomanIndexNumbering: false,
      continuePageNumberingAfterIndex: false,
      repeatIndexPageTitle: false,
      columns: [...BUNDLE_INDEX_COLUMN_IDS],
    },
  };
}

export function buildBundlePreviewEntries(items: BundleItem[], startingPage = 1): BundlePreviewEntry[] {
  const rows = buildBundleIndexRows(
    items.map((item) => ({
      name: item.originalName,
      customName: item.customName,
      dateText: item.dateText,
      pageCount: item.pageCount ?? 1,
    })),
    startingPage,
  );

  return items.map((item, index) => ({
    id: item.id,
    index,
    item,
    row: rows[index],
  }));
}

export function getBundlePreviewStats(items: BundleItem[]): BundlePreviewStats {
  const documentPages = items.reduce((total, item) => total + (item.pageCount ?? 1), 0);

  return {
    itemCount: items.length,
    documentPages,
    totalPagesWithContents: documentPages + 1,
  };
}
